import React, { lazy, useEffect } from "react";

import { Routes, Route, Navigate } from "react-router-dom";

import Home from "./page/Home";
import Brand from "./page/Brands";
import Category from "./page/Categories";
import ProductsDetail from "./page/Product";
import Products from "./page/Products";
import OrderResume from "./page/OrderResume";
import OrderSummary from "./page/OrderSummary";
import OrderResumePag from "./page/OrderResumePag";
import OrderResumeStatus from "./page/OrderResumeStatus";
import CheckoutRouter from "./page/CheckoutRouter";
import RechargeTV from "./page/RechargeTV";
import RechargeCell from "./page/RechargeCell";
import TicketSearch from "./page/TicketSearch";
import SearchResultTravel from "./page/SearchResultTravel";
import FlightDetails from "./page/FlightDetails";
import PointsTransfer from "./page/PointsTransfer";
import OrderPlaced from "./page/OrderPlaced";
import OrderNotApproved from "./page/OrderNotApproved";
import PixConfirmation from "./page/PixConfirmation";
import OrderPlacedSuccessfully from "./page/OrderPlacedSuccessfully";
import Investir from "./page/Investir";
import AuthRedirectComponent from "./page/Authenticate/auth";
import MyAccount from "./page/MyAccount";
import MyAccountPj from "./page/MyAccountPJ";
import MyRequests from "./page/MyRequests";
import HelpYou from "./page/HelpYou";
import RedemptionReceivedAndApproved from "./page/RedemptionReceivedAndApproved";
import RechargeSummary from "./page/RechargeSummary";
import TransferSummary from "./page/TransferSummary";
import Favorites from "./page/Favorites";
const LazyErrors = lazy(() => import(`./page/Errors`));
import ProductVariation from "./page/ProductVariation";
import Policy from "./page/Policy";
import Exchange from "./page/Exchange";
import ErrorPage from "./page/ErrorPage";
import NotFound from "./page/ErrorPage/notFound";

import Brands from "./components/OpenCategoryOrBrand/SeeAllBrands";
import Categories from "./components/OpenCategoryOrBrand/SeeAllCategory";

import { TravelSearch } from "./page/TravelSearch";
import { HotelsSearchResult } from "./page/HotelsSearchResult";
import { Hotel } from "./page/Hotel";
import { HotelTravellers } from "./page/HotelTravellers";
import { CarSearchResult } from "./page/CarSearchResult";
import { CarConductor } from "./page/CarConductor";
import { CheckoutTravel } from "./page/CheckoutTravel";
import { CheckoutTravelMessage } from "./page/CheckoutTravelMessage";
import ListAddressesPage from "page/ListAddresses";

import SessionExpired from "page/SessionExpired";

import { useDispatch, useSelector } from "react-redux";
import { getTokenExpired, setTokenExpired } from "./Redux/authSlice";
import FlightTickets from "page/FlightTickets/FlightTickets";
import Hotels from "page/Hotels/Hotels";
import ResumoHotel from "page/ResumoHotel";
import FlightResume from "page/FlightResume";
import wlTokenService from "./_services/wlToken.service";
import { removeCustomerFromMemory } from "./lib/sharedSessionMemory";
import RouteGuard from "./routes/RouteGuard";

export default function ConnectionBetweenPages() {
  const isTokenExpired = useSelector(getTokenExpired);
  const dispatch = useDispatch();

  useEffect(() => {
    let isRefreshing = false;

    const checkTokenExpiration = async () => {
      if (isRefreshing) {
        return;
      }

      const currentToken = wlTokenService.getAccessToken();
      const currentRefreshToken = wlTokenService.getRefreshToken();
      const currentPath = window.location.pathname;

      if (currentPath.includes('/loja/auth')) {
        return;
      }

      try {
        if (!currentToken && currentRefreshToken) {
          isRefreshing = true;
          await wlTokenService.refreshToken();
          return;
        }

        if (!currentToken) {
          return;
        }

        if (wlTokenService.isAccessTokenNearExpiration()) {
          isRefreshing = true;
          await wlTokenService.refreshToken();
        }
      } catch (error) {
        console.error('Erro ao verificar expiração do token:', error);
        wlTokenService.clearTokens();
        removeCustomerFromMemory();
        dispatch(setTokenExpired(true));
      } finally {
        isRefreshing = false;
      }
    };

    checkTokenExpiration();

    const expirationInterval = setInterval(() => {
      checkTokenExpiration();
    }, 30 * 1000);

    return () => {
      clearInterval(expirationInterval);
    };
  }, [dispatch]);

  return isTokenExpired ? (
    <SessionExpired />
  ) : (
    <Routes>
      <Route path="/" element={<Navigate to="/loja" />} />
      <Route path="loja/auth" element={<AuthRedirectComponent />} />
      <Route
        path="loja/Investir/"
        element={
          <RouteGuard flag="investment">
            <Investir />
          </RouteGuard>
        }
      />
      <Route path="loja/" element={<Home />} />
      <Route path="loja/brands/:id" element={<Brand />} />
      <Route path="loja/brands/" element={<Brands />} />
      <Route path="loja/category/:id" element={<Category />} />
      <Route path="loja/categories/" element={<Categories />} />
      <Route path="loja/product/:id" element={<ProductsDetail />} />
      <Route path="loja/products/:desc" element={<Products />} />
      <Route path="loja/orderresume/" element={<OrderResume />} />
      <Route path="loja/ordersummary/" element={<OrderSummary />} />
      <Route path="loja/list-addresses/" element={<ListAddressesPage />} />
      <Route path="loja/orderresumepag/" element={<CheckoutRouter />} />
      <Route
        path="loja/orderresumestatus/:id"
        element={<OrderResumeStatus />}
      />
      <Route
        path="loja/pixconfirmation/:id"
        element={<PixConfirmation />}
      />
      <Route path="loja/passagens-aereas" element={<FlightTickets />} />
      <Route path="loja/passagens-aereas/voos" element={<FlightResume />} />
      <Route
        path="loja/recharge-tv/"
        element={
          <RouteGuard flag="recharges">
            <RechargeTV />
          </RouteGuard>
        }
      />
      <Route
        path="loja/recharge-cell/"
        element={
          <RouteGuard flag="recharges">
            <RechargeCell />
          </RouteGuard>
        }
      />
      <Route path="loja/searchresulttravel/" element={<SearchResultTravel />} />
      <Route path="loja/flightdetails/" element={<FlightDetails />} />
      <Route
        path="loja/points-transfer/"
        element={
          <RouteGuard flag="points_transfer">
            <PointsTransfer />
          </RouteGuard>
        }
      />
      <Route path="loja/orderplaced/" element={<OrderPlaced />} />
      <Route path="loja/ordernotapproved/" element={<OrderNotApproved />} />
      <Route
        path="loja/orderplacedsuccessfully/"
        element={<OrderPlacedSuccessfully />}
      />
      <Route path="loja/myaccount/" element={<MyAccount />} />
      <Route
        path="loja/pj/myaccount/"
        element={
          <RouteGuard flag="pj_account">
            <MyAccountPj />
          </RouteGuard>
        }
      />
      <Route path="loja/myrequests/" element={<MyRequests />} />
      <Route path="loja/myrequests/help/:uuid" element={<HelpYou />} />
      <Route
        path="loja/redemptionreceivedandapproved/"
        element={<RedemptionReceivedAndApproved />}
      />
      <Route
        path="loja/rechargesummary/"
        element={
          <RouteGuard flag="recharges">
            <RechargeSummary />
          </RouteGuard>
        }
      />
      <Route
        path="loja/transfersummary/"
        element={
          <RouteGuard flag="points_transfer">
            <TransferSummary />
          </RouteGuard>
        }
      />
      <Route
        path="loja/favorites/"
        element={
          <RouteGuard flag="favorites">
            <Favorites />
          </RouteGuard>
        }
      />
      <Route path="loja/productvariation/" element={<ProductVariation />} />
      <Route path="loja/policy/" element={<Policy />} />
      <Route path="loja/exchange/" element={<Exchange />} />
      <Route path="loja/error/" element={<ErrorPage />} />
      <Route
        path="loja/search-travel"
        element={
          <RouteGuard flag="travel">
            <TravelSearch />
          </RouteGuard>
        }
      />
      <Route
        path="loja/search-hotels-result"
        element={
          <RouteGuard flag="hotels">
            <HotelsSearchResult />
          </RouteGuard>
        }
      />
      <Route
        path="loja/hoteis"
        element={
          <RouteGuard flag="hotels">
            <Hotels />
          </RouteGuard>
        }
      />
      <Route path="loja/resumo-hotel/:uuid" element={<ResumoHotel />} />
      <Route
        path="loja/hotel"
        element={
          <RouteGuard flag="hotels">
            <Hotel />
          </RouteGuard>
        }
      />
      <Route
        path="loja/hotel-travellers"
        element={
          <RouteGuard flag="hotels">
            <HotelTravellers />
          </RouteGuard>
        }
      />
      <Route
        path="loja/search-cars-result"
        element={
          <RouteGuard flag="car_rental">
            <CarSearchResult />
          </RouteGuard>
        }
      />
      <Route
        path="loja/car-conductor"
        element={
          <RouteGuard flag="car_rental">
            <CarConductor />
          </RouteGuard>
        }
      />
      <Route
        path="loja/checkout-travel/:product"
        element={
          <RouteGuard flag="travel">
            <CheckoutTravel />
          </RouteGuard>
        }
      />
      <Route
        path="loja/checkout-message-travel/:id"
        element={
          <RouteGuard flag="travel">
            <CheckoutTravelMessage />
          </RouteGuard>
        }
      />
      <Route
        path="loja/checkout-message-travel"
        element={
          <RouteGuard flag="travel">
            <CheckoutTravelMessage />
          </RouteGuard>
        }
      />
      <Route path="loja/notFound/" element={<NotFound />} />
      <Route path="*" element={<LazyErrors />} />
    </Routes>
  );
}
